import React, { useCallback, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, TextField, Typography
} from "@mui/material";
import {
  CartesianGrid, Legend, Line, LineChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import { fetchEventActivity } from "../../../services/dashboardModule/dashboardServices.js";
// Accounts comes from the SAME endpoint the Scorecard and Trends pages use, so
// "Accounts" means one thing across the app: distinct Accountid in
// customerconvevent over the window. See the ⚠ on the fetch below — it is not
// DPD-filtered, and this page is.
import { getDashboardAccountsHourly as getAccounts }
  from "../../../services/pbi/pbiservices.js";
import DatePickerComponent from "../../../common/components/DatePickerComponent.jsx";
import { STRINGS } from "../../../common/constants/commonConstants.js";
import { formatDateTime } from "../../../common/constants/dashboardModule/activityConstants.js";

// ── THE NAMING, WRITTEN DOWN ───────────────────────────────────────
//
// These five terms mean the same thing on every page and in the emailed
// report. Two of them look almost identical and are not:
//
//   Response %     Responses / Text Sent        did the message land
//   RPC %          Responses / Accounts         did we reach the account
//
// One message per send, but an account can take several sends in a window, so
// the two denominators differ and so do the rates. This file previously
// labelled Responses/Sent as "RPC %", which is the other one.
//
//   PTP            Promises + Kept              everything committed to
//   Kept %         Kept / PTP                   how much of it arrived
//   Uncommitted %  Uncommitted / RPC            measured against reach, not
//                                               against PTP
//
// ⚠ Uncommitted is read against RPC rather than PTP on purpose, so it does NOT
// complete to 100% with Kept %. They answer different questions.
//
// ── THE PALETTE IS THE REPORT'S ──
// rpt1's PDF is the artifact people forward, so it is the reference: RPC blue,
// PTP green, Kept a darker shade of that green, Accounts brown. A metric reads
// the same hue here, on the dashboards, and in the PDF.
const shade = (hex, pct) => {
  const n = parseInt(String(hex).slice(1), 16);
  const f = (c) => Math.max(0, Math.min(255, Math.round(c + c * pct)));
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255]
    .map(f).map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};
const KEPT_SHADE = -0.42;

const C_SENT        = "#0C447C";  // navy
const C_ACCOUNTS    = "#8A5524";  // brown
const C_RPC         = "#378ADD";  // blue
const C_PTP         = "#1D9E75";  // green
const C_SL          = "#D4537E";  // pink
const C_KEPT        = shade(C_PTP, KEPT_SHADE);   // dark green

// Kept for the Search button and other chrome that names no metric.
const BLUE = C_RPC;

// ── Helpers ────────────────────────────────────────────────────────
function fmtCurrency(n) {
  const num = Number(n);
  if (n === null || n === undefined || isNaN(num)) return "$0";
  if (Math.abs(num) >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
  if (Math.abs(num) >= 1000)    return `$${(num / 1000).toFixed(2)}K`;
  return `$${num.toFixed(2)}`;
}

function fmtDate(val) {
  if (!val) return "";
  // Parse YYYY-MM-DD as a local date — not UTC (see Payments.jsx).
  if (typeof val === "string") {
    const m = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const d = new Date(+m[1], +m[2] - 1, +m[3]);
      if (!isNaN(d)) {
        return d.toLocaleDateString("en-US", {
          month: "2-digit", day: "2-digit", year: "2-digit",
        });
      }
    }
  }
  const d = new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
  });
}

// Serialised from the date parts — toISOString() converts to UTC first, which
// shifts the date by a day in negative-offset zones. The accounts route wants
// YYYY-MM-DD; formatDateTime is left to the call it already serves, so the two
// routes keep the argument shape each expects.
function toLocalYMD(d) {
  if (!(d instanceof Date) || isNaN(d)) return "";
  const yy = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mo}-${dy}`;
}

function safeNum(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

// Axis date label: "Tu 06/16" — two-char weekday + MM/DD, no year
// (matches TrendsDashboard). Table rows keep the full MM/DD/YY format.
function fmtAxisDate(val) {
  if (!val) return "";
  const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
  if (isNaN(d)) return String(val);
  const wd = ["Su","Mo","Tu","We","Th","Fr","Sa"][d.getDay()];
  const md = d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" });
  return `${wd} ${md}`;
}

// ── Sub-components ─────────────────────────────────────────────────
function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

function KpiTile({ label, value, sub, color = C_RPC }) {
  return (
    <Box sx={{
      background: "#f7f7f7", borderRadius: 2,
      p: "10px 12px",
      flex: 1, minWidth: 0,
      display: "flex", flexDirection: "column", gap: "6px",
    }}>
      <Typography sx={{
        fontSize: 10, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 20, fontWeight: 500, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography sx={{
        fontSize: 12,
        color: sub ? "text.primary" : "text.disabled",
        lineHeight: 1.2,
      }}>
        {sub || "—"}
      </Typography>
    </Box>
  );
}

function ChartCard({ title, children, height = 240 }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px" }}>
      {title && (
        <Typography sx={{
          fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
        }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Box>
  );
}

// ── Main Component ─────────────────────────────────────────────────
const EventActivity = () => {
  const [startDate, setStartDate] = useState(() => new Date());
  const [endDate,   setEndDate]   = useState(() => new Date());
  const [dpdMin,    setDpdMin]    = useState(1);
  const [dpdMax,    setDpdMax]    = useState(5);
  const [data,      setData]      = useState([]);
  // Accounts worked in the window. Its own endpoint, its own state — the
  // distinct scan is too costly to ride along on the activity read, which is
  // why every other page fetches it separately too.
  const [accounts,  setAccounts]  = useState(0);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [searched,  setSearched]  = useState(false);

  const load = useCallback(async () => {
    if (Number(dpdMin) > Number(dpdMax)) {
      setError("DPD From must be less than or equal to DPD To.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      // ⚠ THE ACCOUNTS CALL IS NOT DPD-FILTERED. /dashboard/hourly/accounts
      // takes a date window and a channel, and nothing else — it counts every
      // account worked, while every other figure on this page is scoped to the
      // DPD range in the filter bar.
      //
      // So RPC % (Responses / Accounts) reads LOW here whenever the DPD range
      // is narrower than the book, and the narrower the range the lower it
      // reads. The tile says so, and the caption under the KPI row says so
      // again, because a rate that is wrong by an unknown factor is worse than
      // no rate at all if nobody knows it is.
      //
      // The fix is a dpdMin/dpdMax pair on that route; until then the figure
      // is honest only when the range covers everything.
      //
      // Failure degrades to 0 rather than taking the page down: the tile then
      // reads "—" and the activity data still renders.
      const [result, acc] = await Promise.all([
        fetchEventActivity({
          startDate: formatDateTime(startDate),
          endDate:   formatDateTime(endDate),
          dpdMin:    Number(dpdMin),
          dpdMax:    Number(dpdMax),
        }),
        getAccounts({
          startDate:   toLocalYMD(startDate),
          endDate:     toLocalYMD(endDate),
          granularity: "day",
          channel:     "texts",
        }).catch(() => ({})),
      ]);
      setData(result || []);
      setAccounts(safeNum(acc?.account_count));
      setSearched(true);
    } catch (e) {
      console.error(e);
      setError("Failed to load event activity data.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, dpdMin, dpdMax]);

  // ── KPI totals across the full result set ────────────────────────
  //
  // ⚠ `row.PTP` IS THE PROMISE HALF, NOT THE WHOLE PTP. The column name says
  // otherwise, which is why this is spelled out: PTP the METRIC is Promises +
  // Kept, so it is row.PTP + row.Payments. The Kept % denominator was already
  // written that way before the labels were fixed.
  //
  // `uncomm` is that promise half. The name is historical; it is Promises,
  // and nothing displays it.
  const kpis = useMemo(() => {
    const sent      = data.reduce((a,b) => a + safeNum(b.TextSent),    0);
    const responses = data.reduce((a,b) => a + safeNum(b.Responses),   0);
    const kept      = data.reduce((a,b) => a + safeNum(b.Payments),    0);
    const uncomm    = data.reduce((a,b) => a + safeNum(b.PTP),         0);
    const amount    = data.reduce((a,b) => a + safeNum(b.TotalAmount), 0);

    const ptp = kept + uncomm;

    return {
      sent, responses, kept, uncomm, ptp, amount,
      // Responses / Text Sent — did the message land.
      responsePct: sent ? (responses / sent) * 100 : 0,
      // Responses / Accounts — did we reach the account. An account can take
      // several sends in a window, so this denominator is smaller than `sent`
      // and this rate is higher than Response %.
      rpcPct: accounts ? (responses / accounts) * 100 : 0,
      // PTP / RPC — of the accounts reached, how many committed to something.
      ptpPct: responses ? (ptp / responses) * 100 : 0,
      // Kept / PTP — of everything committed to, how much arrived.
      keptPct: ptp ? (kept / ptp) * 100 : 0,
      // Accounts / Text Sent — not a conversion, a DENSITY: how many sends it
      // took per account. Below 100% means accounts took more than one send.
      accountsPct: sent ? (accounts / sent) * 100 : 0,
    };
  }, [data, accounts]);

  // Chart data — pivot date×DPD rows into one point per date, with two
  // keys per DPD value: `dpd_N` (Response pct) and `sent_N` (texts sent).
  // Feeds both line charts from a single pass. Days where a DPD had zero
  // sends leave the pct undefined (gap in the line) rather than plotting
  // a misleading 0%.
  //
  // ⚠ THIS IS RESPONSE %, NOT RPC %. The denominator is sends, because
  // accounts are not available per (date, DPD) — the accounts endpoint returns
  // one number for the whole window. Charting RPC % here would need a
  // per-day-per-DPD account count that nothing currently produces.
  const { chartData, dpdValues } = useMemo(() => {
    const map  = {};
    const dpds = new Set();
    data.forEach(r => {
      // Key by raw ISO date so sorting stays correct; `date` holds the
      // axis label ("Tu 06/16") the charts display.
      const key = String(r.EventDate).slice(0, 10);
      if (!map[key]) map[key] = { rawDate: key, date: fmtAxisDate(r.EventDate) };
      if (r.DPD === null || r.DPD === undefined) return;
      dpds.add(r.DPD);
      const sent = safeNum(r.TextSent);
      map[key][`sent_${r.DPD}`] = sent;
      if (sent > 0) {
        map[key][`dpd_${r.DPD}`] = Number(
          ((safeNum(r.Responses) / sent) * 100).toFixed(1)
        );
      }
    });
    return {
      chartData: Object.values(map).sort((a,b) => a.rawDate.localeCompare(b.rawDate)),
      dpdValues: [...dpds].sort((a,b) => a - b),
    };
  }, [data]);

  // ── ONE COLOUR PER DPD, AND DELIBERATELY NOT THE METRIC PALETTE ──
  // These lines are CATEGORIES — one per days-past-due value — not metrics, so
  // they want maximum separation rather than the meaning the metric hues
  // carry. Reusing C_PTP here would put a green line labelled "DPD 3" next to
  // a green PTP tile and imply a relationship that does not exist.
  const LINE_COLORS = [
    "#378ADD", "#1D9E75", "#BA7517", "#7F77DD",
    "#D9534F", "#17A2B8", "#8A6D3B", "#5A9E4B",
    "#C2559C", "#4A6FA5", "#B8860B", "#6B5B95",
  ];

  const displayedRows = useMemo(() => data.slice(0, 200), [data]);

  const visibleTotals = useMemo(() => ({
    sent:      displayedRows.reduce((a,b) => a + safeNum(b.TextSent),          0),
    responses: displayedRows.reduce((a,b) => a + safeNum(b.Responses),         0),
    kept:      displayedRows.reduce((a,b) => a + safeNum(b.Payments),          0),
    amount:    displayedRows.reduce((a,b) => a + safeNum(b.TotalAmount),       0),
    sl:        displayedRows.reduce((a,b) => a + safeNum(b.SmartLinks),        0),
    reminders: displayedRows.reduce((a,b) => a + safeNum(b.Reminders),         0),
    ends:      displayedRows.reduce((a,b) => a + safeNum(b.EndOfConversation), 0),
  }), [displayedRows]);

  const axisStyle    = { fontSize: 10, fill: "#888" };
  const tooltipStyle = { fontSize: 12 };

  // "Response %", not "RPC %" — the column is ResponsePct, Responses over
  // Sent. The old header named the other rate.
  const COLS = "90px 55px 85px 90px 100px 100px 85px 90px 90px 100px";
  const HEADERS = [
    "Date", "DPD", "Text Sent", "RPC", "Response %", "PTP $",
    "Same Day", "Smartlinks", "Reminders", "End of Conv",
  ];

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Filters ─────────────────────────────────────────── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2,
        alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <DatePickerComponent
          label={STRINGS.START_DATE}
          date={startDate}
          onDateChange={setStartDate}
          marginRight={0}
        />
        <DatePickerComponent
          label={STRINGS.END_DATE}
          date={endDate}
          onDateChange={d => { if (d >= startDate) setEndDate(d); }}
          marginRight={0}
        />
        <TextField
          label="DPD From"
          type="number"
          size="small"
          value={dpdMin}
          onChange={e => setDpdMin(e.target.value)}
          inputProps={{ min: 0 }}
          sx={{ width: 100 }}
        />
        <TextField
          label="DPD To"
          type="number"
          size="small"
          value={dpdMax}
          onChange={e => setDpdMax(e.target.value)}
          inputProps={{ min: 0 }}
          sx={{ width: 100 }}
        />
        <Box
          onClick={load}
          sx={{
            px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
            cursor: "pointer", userSelect: "none",
            background: BLUE, color: "#fff", fontWeight: 500,
            alignSelf: "flex-end", mb: "1px",
            "&:hover": { background: "#2a6db5" },
          }}
        >
          Search
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
          <CircularProgress size={20} /> Loading…
        </Box>
      )}

      {!loading && searched && data.length === 0 && !error && (
        <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
          No data for selected range.
        </Typography>
      )}

      {!loading && data.length > 0 && (
        <>
          {/* ── KPI TILES ─────────────────────────────────────── */}
          <SectionTitle>Summary — DPD {dpdMin}–{dpdMax}</SectionTitle>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
            {/* ── COUNTS LEAD, MONEY LEADS ──
                Sent, Accounts and RPC are populations, so the count is the
                headline and the rate sits under it. PTP and Kept are money, so
                the dollars lead and the count moves into the sub beside the
                rate — those two tiles answer "how much", not "how many". */}
            <KpiTile label="Text Sent"
              value={kpis.sent.toLocaleString()}
              sub={`${kpis.responsePct.toFixed(1)}% responded`}
              color={C_SENT} />
            {/* Accounts sits directly after Sent on every page that has it, so
                the two denominators the rates below use are read in the order
                the rates use them.

                Its sub is a DENSITY, not a conversion: accounts over sends, so
                60% means an account took roughly 1.7 messages. The "all DPD"
                half is the warning that matters more — see the caption. */}
            <KpiTile label="Accounts"
              value={accounts ? accounts.toLocaleString() : "—"}
              sub={accounts ? `${kpis.accountsPct.toFixed(1)}% of sent · all DPD`
                            : "unavailable"}
              color={C_ACCOUNTS} />
            <KpiTile label="RPC"
              value={kpis.responses.toLocaleString()}
              sub={accounts ? `${kpis.rpcPct.toFixed(1)}% of accounts` : "—"}
              color={C_RPC} />
            <KpiTile label="PTP"
              value={fmtCurrency(kpis.amount)}
              sub={`${kpis.ptp.toLocaleString()} · ${kpis.ptpPct.toFixed(1)}% of RPC`}
              color={C_PTP} />
            {/* ⚠ NO KEPT $ IN THIS DATASET. The query returns one amount —
                TotalAmount, which is PTP — so Kept has a count and no dollars.
                The tile therefore leads with the count while every other money
                tile leads with dollars, which is the one place this row breaks
                its own rule.
                Fix is a KeptAmount column on the event-activity query; then
                this becomes fmtCurrency(kpis.keptAmount) and the count joins
                the sub, exactly like PTP above. */}
            <KpiTile label="Same Day"
              value={kpis.kept.toLocaleString()}
              sub={`${kpis.keptPct.toFixed(1)}% of PTP`}
              color={C_KEPT} />
          </Box>

          <Typography sx={{
            fontSize: 11, color: "text.disabled", mb: 2, lineHeight: 1.5,
          }}>
            <b>Response %</b> is responses over texts sent; <b>RPC %</b> is responses over
            accounts — an account can take several sends in a window, so the two differ.
            <b> PTP</b> is Same Day + Promises, read against RPC; <b>Same Day %</b> is Same
            Day over PTP. Promises are future payments scheduled through PNM; they feed PTP and
            are not shown on their own. Same Day shows a count rather than a dollar figure because this query returns
            only one amount, which is PTP.
            {accounts > 0 && (
              <> {" "}⚠ <b>Accounts is not filtered by DPD</b> — it counts every account
              worked in the window, while every other figure here is scoped to DPD{" "}
              {dpdMin}–{dpdMax}. RPC % therefore reads low unless the DPD range covers
              the whole book.</>
            )}
          </Typography>

          {/* ── CHART — Response pct by DPD, one line per DPD ── */}
          <SectionTitle>Response percentage by DPD</SectionTitle>
          <ChartCard height={280}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis
                  tick={axisStyle}
                  tickFormatter={v => `${v}%`}
                  domain={[0, "auto"]}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v, n) => [`${v}%`, n]}
                />
                <Legend iconSize={10} iconType="plainline" wrapperStyle={{ fontSize: 11 }} />
                {dpdValues.map((dpd, i) => (
                  <Line
                    key={dpd}
                    type="monotone"
                    dataKey={`dpd_${dpd}`}
                    name={`DPD ${dpd}`}
                    stroke={LINE_COLORS[i % LINE_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 2.5 }}
                    connectNulls={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── CHART — texts sent by DPD, one line per DPD ──── */}
          <SectionTitle>Texts sent by DPD</SectionTitle>
          <ChartCard height={280}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis
                  tick={axisStyle}
                  tickFormatter={v => v.toLocaleString()}
                  domain={[0, "auto"]}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v, n) => [Number(v).toLocaleString(), n]}
                />
                <Legend iconSize={10} iconType="plainline" wrapperStyle={{ fontSize: 11 }} />
                {dpdValues.map((dpd, i) => (
                  <Line
                    key={dpd}
                    type="monotone"
                    dataKey={`sent_${dpd}`}
                    name={`DPD ${dpd}`}
                    stroke={LINE_COLORS[i % LINE_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 2.5 }}
                    connectNulls={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── DETAIL TABLE — all SQL columns ────────────────── */}
          <SectionTitle>Detail</SectionTitle>
          <Box sx={{
            border: "0.5px solid", borderColor: "divider",
            borderRadius: 2, overflow: "auto",
          }}>
            <Box sx={{
              display: "grid",
              gridTemplateColumns: COLS,
              background: "#f7f7f7",
              borderBottom: "0.5px solid", borderColor: "divider",
              px: 2, py: 1,
              fontSize: 11, fontWeight: 500,
              color: "text.secondary",
              textTransform: "uppercase", letterSpacing: "0.05em",
            }}>
              {HEADERS.map((h, i) => (
                <Box key={h} sx={{ textAlign: i === 0 ? "left" : "right" }}>{h}</Box>
              ))}
            </Box>

            {displayedRows.map((row, i) => (
              <Box key={i} sx={{
                display: "grid",
                gridTemplateColumns: COLS,
                px: 2, py: "6px",
                fontSize: 12,
                borderBottom: "0.5px solid", borderColor: "divider",
                background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                "&:hover": { background: "rgba(55,138,221,0.06)" },
              }}>
                <Box>{fmtDate(row.EventDate)}</Box>
                <Box sx={{ textAlign: "right" }}>{row.DPD ?? "—"}</Box>
                <Box sx={{ textAlign: "right", color: C_SENT, fontWeight: 500 }}>
                  {safeNum(row.TextSent).toLocaleString()}
                </Box>
                <Box sx={{ textAlign: "right", color: C_RPC, fontWeight: 500 }}>
                  {safeNum(row.Responses).toLocaleString()}
                </Box>
                {/* ResponsePct is Responses/Sent — the header names it
                    correctly now. There is no per-row RPC %: accounts are not
                    available per (date, DPD). */}
                <Box sx={{ textAlign: "right", color: C_RPC }}>
                  {row.ResponsePct !== null && row.ResponsePct !== undefined
                    ? `${Number(row.ResponsePct).toFixed(1)}%`
                    : "—"}
                </Box>
                <Box sx={{ textAlign: "right", color: C_PTP, fontWeight: 500 }}>
                  {row.TotalAmount !== null && row.TotalAmount !== undefined
                    ? fmtCurrency(row.TotalAmount)
                    : "—"}
                </Box>
                <Box sx={{ textAlign: "right", color: C_KEPT }}>
                  {safeNum(row.Payments).toLocaleString()}
                </Box>
                <Box sx={{ textAlign: "right", color: C_SL }}>
                  {safeNum(row.SmartLinks).toLocaleString()}
                </Box>
                <Box sx={{ textAlign: "right" }}>{safeNum(row.Reminders).toLocaleString()}</Box>
                <Box sx={{ textAlign: "right" }}>{safeNum(row.EndOfConversation).toLocaleString()}</Box>
              </Box>
            ))}

            {/* Totals row */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: COLS,
              px: 2, py: "8px",
              fontSize: 12, fontWeight: 600,
              borderTop: "1px solid", borderColor: "divider",
              background: "#f0f3f7",
            }}>
              <Box>Total</Box>
              <Box sx={{ textAlign: "right" }}>—</Box>
              <Box sx={{ textAlign: "right", color: C_SENT }}>{visibleTotals.sent.toLocaleString()}</Box>
              <Box sx={{ textAlign: "right", color: C_RPC  }}>{visibleTotals.responses.toLocaleString()}</Box>
              <Box sx={{ textAlign: "right", color: C_RPC  }}>
                {visibleTotals.sent
                  ? `${((visibleTotals.responses / visibleTotals.sent) * 100).toFixed(1)}%`
                  : "—"}
              </Box>
              <Box sx={{ textAlign: "right", color: C_PTP }}>{fmtCurrency(visibleTotals.amount)}</Box>
              <Box sx={{ textAlign: "right", color: C_KEPT }}>{visibleTotals.kept.toLocaleString()}</Box>
              <Box sx={{ textAlign: "right", color: C_SL }}>{visibleTotals.sl.toLocaleString()}</Box>
              <Box sx={{ textAlign: "right" }}>{visibleTotals.reminders.toLocaleString()}</Box>
              <Box sx={{ textAlign: "right" }}>{visibleTotals.ends.toLocaleString()}</Box>
            </Box>
          </Box>

          {data.length > displayedRows.length && (
            <Typography sx={{
              fontSize: 11, color: "text.secondary",
              mt: 1, textAlign: "right",
            }}>
              Showing {displayedRows.length.toLocaleString()} of {data.length.toLocaleString()} rows.
              KPI cards and chart above reflect the full range.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default EventActivity;